import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import {
  Ticket,
  Key,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  Package,
} from 'lucide-react';
import { cn } from '../utils/cn';
import AiHealthCard from '../components/shared/AiHealthCard';

function StatCard({ label, value, icon: Icon, color, to, viewAllLabel }) {
  const Wrapper = to ? Link : 'div';
  return (
    <Wrapper
      to={to}
      className={cn(
        'bg-white rounded-xl border border-gray-200 p-6 shadow-sm',
        to && 'hover:border-primary-300 hover:shadow-md transition-all'
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', color)}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      {to && (
        <div className="mt-3 flex items-center text-sm text-primary-600 font-medium">
          {viewAllLabel} <ArrowUpRight className="h-4 w-4 ml-1" />
        </div>
      )}
    </Wrapper>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">{t('dashboard.load_failed')}</div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('dashboard.title')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('dashboard.open_tickets')}
          value={stats.open_tickets}
          icon={Ticket}
          color="bg-blue-100 text-blue-600"
          to="/tickets?status=new"
          viewAllLabel={t('common.view_all')}
        />
        <StatCard
          label={t('dashboard.resolved')}
          value={stats.resolved_tickets}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          viewAllLabel={t('common.view_all')}
        />
        <StatCard
          label={t('dashboard.escalated')}
          value={stats.escalated_tickets}
          icon={AlertTriangle}
          color="bg-orange-100 text-orange-600"
          to="/tickets?status=escalated"
          viewAllLabel={t('common.view_all')}
        />
        <StatCard
          label={t('dashboard.available_keys')}
          value={stats.available_keys}
          icon={Key}
          color="bg-purple-100 text-purple-600"
          to="/keys"
          viewAllLabel={t('common.view_all')}
        />
      </div>

      <div className="mb-8">
        <AiHealthCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recent_tickets')}</h2>
            <Link to="/tickets" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              {t('common.view_all')}
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.recent_tickets?.length > 0 ? (
              stats.recent_tickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                  className="flex items-center gap-4 px-6 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{ticket.subject}</p>
                    <p className="text-xs text-gray-500">{ticket.customer_email}</p>
                  </div>
                  <StatusBadge status={ticket.status} t={t} />
                </Link>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-sm text-gray-500">
                {t('dashboard.no_tickets_yet')}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.low_stock_alerts')}</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.low_stock_products?.length > 0 ? (
              stats.low_stock_products.map((product) => (
                <div key={product.id} className="flex items-center gap-4 px-6 py-3">
                  <Package className="h-5 w-5 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{product.name}</p>
                    <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                  </div>
                  <span className={cn(
                    'text-sm font-semibold',
                    product.stock === 0 ? 'text-red-600' : 'text-orange-600'
                  )}>
                    {t('dashboard.keys_count', { count: product.stock })}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
                <CheckCircle className="h-8 w-8 text-green-400" />
                {t('dashboard.all_well_stocked')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const styles = {
    new: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    waiting_customer: 'bg-purple-100 text-purple-700',
    info_requested: 'bg-indigo-100 text-indigo-700',
    resolved: 'bg-green-100 text-green-700',
    escalated: 'bg-red-100 text-red-700',
  };

  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || 'bg-gray-100 text-gray-700')}>
      {t(`status.${status}`, { defaultValue: status?.replace('_', ' ') })}
    </span>
  );
}
